import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Loading } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { GraduationCap, Clock, ListChecks, PenLine, Users, Plus, Trash2, Check, X, Pencil } from "lucide-react";

function GroupEditor({ programs, students, initial, onDone, onCancel }) {
  const [name, setName] = useState(initial?.name || "");
  const [program, setProgram] = useState(initial?.program || "");
  const [sel, setSel] = useState(initial?.student_ids || []);
  const progStudents = students.filter((s) => s.program === program);

  const save = async () => {
    if (!name.trim()) return toast.error("Give the group a name.");
    if (!program) return toast.error("Choose a program.");
    try {
      if (initial?.id) await api.put(`/tutor/groups/${initial.id}`, { name, program, student_ids: sel });
      else await api.post("/tutor/groups", { name, program, student_ids: sel });
      toast.success(initial?.id ? "Group updated" : "Group created");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="ember-card p-6" data-testid="group-editor">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label>Group name</Label>
          <Input data-testid="group-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. JMSS Tue 6pm" className="mt-1.5 h-11 rounded-xl" />
        </div>
        <div>
          <Label>Program</Label>
          <Select value={program} onValueChange={(p) => { setProgram(p); setSel([]); }}>
            <SelectTrigger data-testid="group-program" className="mt-1.5 h-11 rounded-xl"><SelectValue placeholder="Choose program" /></SelectTrigger>
            <SelectContent>{programs && Object.values(programs).map((p) => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {program && (
        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between">
            <Label>Students <span className="font-normal text-[#948EAB]">({sel.length} selected)</span></Label>
            <button onClick={() => setSel(progStudents.map((s) => s.id))} className="text-xs font-semibold text-[#7C3AED] hover:underline">Select all</button>
          </div>
          {progStudents.length === 0 ? (
            <p className="text-sm text-[#948EAB]">No students in this program yet.</p>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {progStudents.map((s) => {
                const on = sel.includes(s.id);
                return (
                  <button key={s.id} data-testid={`group-pick-${s.id}`} onClick={() => setSel(on ? sel.filter((x) => x !== s.id) : [...sel, s.id])}
                    className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors ${on ? "border-[#7C3AED] bg-[#FAF9FE]" : "border-[#EEEAF8] hover:border-[#C4B5FD]"}`}>
                    <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-md border-2 ${on ? "border-[#7C3AED] bg-[#7C3AED] text-white" : "border-[#DDD6FE] text-transparent"}`}><Check className="h-3.5 w-3.5" /></span>
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[#1E1B2E]">{s.name}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
      <div className="mt-5 flex gap-2">
        <Button onClick={save} data-testid="group-save" className="h-10 rounded-xl ember-gradient font-bold">{initial?.id ? "Save group" : "Create group"}</Button>
        <Button variant="outline" onClick={onCancel} className="h-10 rounded-xl"><X className="mr-1.5 h-4 w-4" /> Cancel</Button>
      </div>
    </div>
  );
}

export default function Classes() {
  const [programs, setPrograms] = useState(null);
  const [students, setStudents] = useState([]);
  const [groups, setGroups] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | group object

  const loadGroups = () => api.get("/tutor/groups").then((r) => setGroups(r.data));
  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data));
    api.get("/tutor/students").then((r) => setStudents(r.data));
    loadGroups();
  }, []);
  if (!programs || groups === null) return <Loading />;

  const nameOf = (id) => students.find((s) => s.id === id)?.name || "—";
  const del = async (gid) => {
    if (!window.confirm("Delete this group? Students already assigned to tests stay assigned.")) return;
    try { await api.delete(`/tutor/groups/${gid}`); toast.success("Group deleted"); loadGroups(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Classes" subtitle="Programs, assessment structure and your reusable class groups." />

      {/* Class groups */}
      <div className="mb-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-[#1E1B2E]"><Users className="h-5 w-5 text-[#7C3AED]" /> Class groups</h2>
          {!editing && <Button onClick={() => setEditing("new")} data-testid="new-group-btn" className="h-10 rounded-xl ember-gradient font-bold"><Plus className="mr-1.5 h-4 w-4" /> New group</Button>}
        </div>

        {editing === "new" && <GroupEditor programs={programs} students={students} onDone={() => { setEditing(null); loadGroups(); }} onCancel={() => setEditing(null)} />}

        <div className="mt-4 grid gap-3">
          {groups.length === 0 && editing !== "new" && (
            <div className="ember-card py-12 text-center text-sm text-[#948EAB]">No groups yet. Create one to distribute a test to a whole class in one click.</div>
          )}
          {groups.map((g) => (
            editing && editing.id === g.id ? (
              <GroupEditor key={g.id} programs={programs} students={students} initial={g} onDone={() => { setEditing(null); loadGroups(); }} onCancel={() => setEditing(null)} />
            ) : (
              <div key={g.id} className="ember-card flex items-center gap-4 p-5" data-testid={`group-row-${g.id}`}>
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#EDE9FE] text-[#5B21B6]"><Users className="h-5 w-5" /></span>
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-bold text-[#1E1B2E]">{g.name}</div>
                  <div className="mt-0.5 text-xs text-[#948EAB]">
                    <span className="font-semibold text-[#7C3AED]">{programs[g.program]?.name}</span> · {g.student_ids.length} student{g.student_ids.length === 1 ? "" : "s"}
                    {g.student_ids.length > 0 && <span className="ml-1">— {g.student_ids.slice(0, 3).map(nameOf).join(", ")}{g.student_ids.length > 3 ? `, +${g.student_ids.length - 3}` : ""}</span>}
                  </div>
                </div>
                <button onClick={() => setEditing(g)} data-testid={`edit-group-${g.id}`} className="rounded-lg p-2 text-[#7C3AED] hover:bg-[#F5F3FF]"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(g.id)} data-testid={`delete-group-${g.id}`} className="rounded-lg p-2 text-[#E11D48] hover:bg-[#FFF1F2]"><Trash2 className="h-4 w-4" /></button>
              </div>
            )
          ))}
        </div>
      </div>

      {/* Program structure */}
      <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-extrabold text-[#1E1B2E]"><GraduationCap className="h-5 w-5 text-[#7C3AED]" /> Program structure</h2>
      <div className="grid gap-6 lg:grid-cols-2">
        {Object.values(programs).map((prog) => (
          <div key={prog.key} className="ember-card p-6" data-testid={`class-${prog.key}`}>
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-xl ember-gradient text-white"><GraduationCap className="h-5 w-5" /></span>
              <div>
                <h2 className="font-heading text-xl font-extrabold text-[#1E1B2E]">{prog.name}</h2>
                <p className="text-xs font-semibold text-[#7C3AED]">{prog.sections.length} assessment sections</p>
              </div>
            </div>
            <div className="mt-5 space-y-2.5">
              {prog.sections.map((s) => (
                <div key={s.key} className="flex items-center justify-between rounded-xl border border-[#EEEAF8] px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <span className={`grid h-8 w-8 place-items-center rounded-lg ${s.type === "writing" ? "bg-[#FFF1F2] text-[#E11D48]" : "bg-[#EDE9FE] text-[#5B21B6]"}`}>
                      {s.type === "writing" ? <PenLine className="h-4 w-4" /> : <ListChecks className="h-4 w-4" />}
                    </span>
                    <span className="text-sm font-bold text-[#1E1B2E]">{s.name}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs font-semibold text-[#524B6B]">
                    <span>{s.type === "writing" ? "Written task" : `${s.questions} Q`}</span>
                    <span className="inline-flex items-center gap-1 text-[#948EAB]"><Clock className="h-3 w-3" /> {s.minutes}m</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
